import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Package, ArrowLeft, Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewMaterial = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    price_negotiable: false,
    quantity: '',
    unit: 'szt.',
    condition: 'nowy',
    location: '',
    contact_phone: user?.phone || '',
    contact_email: user?.email || '',
  });

  useEffect(() => {
    if (!user) {
      toast.error('Zaloguj się, aby dodać ogłoszenie');
      navigate('/login?redirect=/materials/new');
      return;
    }
    
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/materials/categories`);
        if (res.ok) setCategories(await res.json());
      } catch (err) {
        console.error('Error:', err);
      }
    };
    fetchCategories();
  }, [user, navigate]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (images.length + files.length > 5) {
      toast.error('Maksymalnie 5 zdjęć');
      return;
    }

    setUploadingImages(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`Plik ${file.name} jest za duży (max 5MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API}/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setImages(prev => [...prev, data.url]);
        } else {
          toast.error(`Błąd przesyłania ${file.name}`);
        }
      }
    } catch (err) {
      toast.error('Błąd przesyłania zdjęć');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price || !formData.category) {
      toast.error('Wypełnij wymagane pola (tytuł, cena, kategoria)');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/materials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          images: images,
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Ogłoszenie dodane!');
        navigate(`/materials/${data.material_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Błąd podczas dodawania');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="new-material-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/materials')} 
            className="text-emerald-100 hover:text-white hover:bg-emerald-500/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Powrót
          </Button>
          
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-white" />
            <h1 className="font-heading font-bold text-2xl text-white">Dodaj materiał na sprzedaż</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Szczegóły ogłoszenia</CardTitle>
            <CardDescription>Opisz materiał, który chcesz sprzedać</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title">Tytuł ogłoszenia *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="np. Cegła klinkierowa 500 szt."
                  required
                  data-testid="input-title"
                />
              </div>

              {/* Category */}
              <div>
                <Label>Kategoria *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Wybierz kategorię" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Cena (PLN) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                    data-testid="input-price"
                  />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="price_negotiable"
                      checked={formData.price_negotiable}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, price_negotiable: checked }))}
                    />
                    <Label htmlFor="price_negotiable" className="cursor-pointer">Cena do negocjacji</Label>
                  </div>
                </div>
              </div>

              {/* Quantity & Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="quantity">Ilość</Label>
                  <Input
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="np. 500"
                    data-testid="input-quantity"
                  />
                </div>
                <div>
                  <Label>Jednostka</Label>
                  <Select value={formData.unit} onValueChange={(v) => setFormData(prev => ({ ...prev, unit: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="szt.">sztuk</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="m">metrów</SelectItem>
                      <SelectItem value="m²">m²</SelectItem>
                      <SelectItem value="m³">m³</SelectItem>
                      <SelectItem value="opak.">opakowań</SelectItem>
                      <SelectItem value="kpl.">kompletów</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Condition */}
              <div>
                <Label>Stan materiału</Label>
                <Select value={formData.condition} onValueChange={(v) => setFormData(prev => ({ ...prev, condition: v }))}>
                  <SelectTrigger data-testid="select-condition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nowy">Nowy</SelectItem>
                    <SelectItem value="używany">Używany</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Opis</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Opisz szczegóły: stan, pochodzenie, powód sprzedaży..."
                  rows={5}
                  data-testid="input-description"
                />
              </div>

              {/* Images */}
              <div>
                <Label>Zdjęcia (max 5)</Label>
                <div className="mt-2">
                  {/* Image previews */}
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-4">
                      {images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt={`Zdjęcie ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload button */}
                  {images.length < 5 && (
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImages}
                      />
                      {uploadingImages ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                          <span className="text-slate-600">Przesyłanie...</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-5 h-5 text-slate-400" />
                          <span className="text-slate-600">Kliknij aby dodać zdjęcia</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="location">Lokalizacja *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="np. Warszawa, Mokotów"
                  required
                  data-testid="input-location"
                />
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_phone">Telefon kontaktowy</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="np. 600 700 800"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Email kontaktowy</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/materials')}>
                  Anuluj
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Dodawanie...
                    </>
                  ) : (
                    'Dodaj ogłoszenie'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewMaterial;
